import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button, Loader, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { useTranslation } from 'react-i18next';
import useCallbackAsync from 'hooks/useCallbackAsync';
import api from 'api';
import { User } from 'models/User';
import { Form } from 'react-final-form';
import RadioGroupField from 'components/RadioGroupField';
import ListingContext from '../../../../providers/Listing/Listing.context';
import { getUserFullName, sortUsers } from 'utils';

import './TransferDealToEnterpriseColleagueModal.styles.scss';
import './TransferDealToEnterpriseColleagueModal.styles.responsive.scss';

const getUser = (users: Partial<User>[], userId: string) =>
  users.find(({ _id }) => _id === userId);

type TransferDealToEnterpriseColleagueModalProps = {
  className?: string;
  dealRoomId: string;
} & ModalProps;

const TransferDealToEnterpriseColleagueModal: React.ForwardRefRenderFunction<
  ModalRef,
  TransferDealToEnterpriseColleagueModalProps
> = (props, ref) => {
  const { className, dealRoomId } = props;

  const [users, setUsers] = useState<Partial<User>[]>([]);

  const { onTransferToEnterpriseColleague } = useContext(ListingContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames(
    'transfer-deal-to-enterprise-colleague-modal',
    className,
  );

  const { t } = useTranslation();

  const options = useMemo(
    () =>
      users.map(({ _id, firstName, lastName }) => ({
        value: _id,
        label: getUserFullName(firstName, lastName),
      })),
    [users],
  );

  const [getEnterpriseUsers, isLoadingUsers] = useCallbackAsync(async () => {
    const { data } = await api.dealRoom.getEnterpriseUsers(dealRoomId);

    setUsers(sortUsers(data));
  });

  const noEnterpriseColleagues = useMemo(
    () => !isLoadingUsers && !users.length,
    [isLoadingUsers, users.length],
  );

  const onSubmit = useCallback(
    async ({ userId }: { userId: string }) => {
      const { firstName, lastName } = getUser(users, userId) || {};

      const userFullName = getUserFullName(firstName, lastName);

      const { isSuccessfully } = await onTransferToEnterpriseColleague(
        userId,
        userFullName,
      );

      if (isSuccessfully) modal?.close();
    },
    [modal, onTransferToEnterpriseColleague, users],
  );

  useEffect(() => {
    if (!modal?.openState) return;

    getEnterpriseUsers();
  }, [getEnterpriseUsers, modal?.openState]);

  return (
    <Modal ref={modalRef} className={classes} title={t('transferDeal')}>
      <Form
        onSubmit={onSubmit}
        render={({ handleSubmit, submitting }) => (
          <form onSubmit={handleSubmit}>
            {isLoadingUsers && <Loader />}

            <p>{t('chooseCooleagueYouWantToTransferDeal')}</p>

            {noEnterpriseColleagues ? (
              <p>{t('noAvailableColleagues')}</p>
            ) : (
              <RadioGroupField name="userId" options={options} />
            )}

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>

              <Button
                type="submit"
                disabled={submitting || noEnterpriseColleagues}
              >
                {t('transfer')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(TransferDealToEnterpriseColleagueModal);
